<template>
  <div class="user-profile-container">
    <!-- Header with profile avatar and name -->
    <v-card class="profile-card" elevation="10">
      <!-- Notifications et alertes -->
      <transition name="slide-fade">
        <v-alert v-if="error"
                 dense
                 class="mb-0 rounded-0"
                 color="red"
                 dark
                 border="left">
          {{ error }}
        </v-alert>
      </transition>

      <transition name="slide-fade">
        <v-alert v-if="alertType"
                 dense
                 class="mb-0 rounded-0"
                 :color="alertColor(alertType)"
                 dark
                 border="left"
                 dismissible
                 @input="dismissibleClose">
          {{ alertMessage }}
        </v-alert>
      </transition>

      <!-- Contenu principal -->
      <div class="card-content">
        <!-- Loading indicator -->
        <div v-if="loading" class="loading-container">
          <v-progress-circular
              indeterminate
              color="primary"
              :size="50"
              :width="5"
          ></v-progress-circular>
          <p class="mt-3">Chargement des informations...</p>
        </div>

        <!-- Main content -->
        <template v-if="!loading && userInfo">
          <!-- Profile header with avatar -->
          <div class="profile-header" :class="{ 'locked': userInfo.locked }">
            <div class="avatar-container">
              <div class="avatar">
                <span v-if="userInfo.accountFirstname && userInfo.accountLastname">
                  {{ getInitials(userInfo.accountFirstname, userInfo.accountLastname) }}
                </span>
                <v-icon v-else size="36">mdi-account</v-icon>
              </div>
              <div v-if="userInfo.locked" class="lock-badge">
                <v-icon small>mdi-lock</v-icon>
              </div>
            </div>
            <div class="user-name">
              <h2>{{ formatName(userInfo.accountLastname, userInfo.accountFirstname) }}</h2>
              <div class="user-role">{{ userInfo.userRoles }}</div>
            </div>
          </div>

          <!-- User information sections -->
          <div class="section-card">
            <div class="section-header">
              <v-icon color="primary" x-small>mdi-account-details</v-icon>
              <span>Informations Personnelles</span>
            </div>
            <div class="info-grid">
              <div class="info-item">
                <div class="info-label">
                  <v-icon small color="primary">mdi-badge-account-outline</v-icon>
                  Matricule {{ userInfo.companyName }}
                </div>
                <div class="info-value">{{ userInfo.userIdReal || 'Non renseigné' }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">
                  <v-icon small color="primary">mdi-badge-account</v-icon>
                  Code Utilisateur
                </div>
                <div class="info-value">{{ userInfo.userId || 'Non renseigné' }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">
                  <v-icon small color="primary">mdi-phone</v-icon>
                  Téléphone
                </div>
                <div class="info-value">{{ userInfo.accountPhonenumber || 'Non renseigné' }}</div>
              </div>

              <div class="info-item">
                <div class="info-label">
                  <v-icon small color="primary">mdi-email-open</v-icon>
                  Email Réel
                </div>
                <div class="info-value">{{ userInfo.realAccountEmail || 'Non renseigné' }}</div>
              </div>
            </div>
          </div>

          <div class="section-card">
            <div class="section-header">
              <v-icon color="primary" x-small>mdi-domain</v-icon>
              <span>Informations Professionnelles</span>
            </div>
            <div class="info-grid">
              <div class="info-item">
                <div class="info-label">
                  <v-icon small color="primary">mdi-office-building</v-icon>
                  Entreprise
                </div>
                <div class="info-value">{{ userInfo.companyName || 'Non renseignée' }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">
                  <v-icon small color="primary">mdi-account-group</v-icon>
                  Service
                </div>
                <div class="info-value">{{ userInfo.brigade || 'Non renseignée' }}</div>
              </div>
            </div>
          </div>

          <div class="section-card">
            <div class="section-header">
              <v-icon color="primary" x-small>mdi-wallet</v-icon>
              <span>Portefeuille</span>
            </div>
            <div class="info-grid">
              <div class="info-item">
                <div class="info-label">
                  <v-icon small color="primary">mdi-wallet-outline</v-icon>
                  Identifiant Portefeuille
                </div>
                <div class="info-value">{{ userInfo.walletId || 'Non renseigné' }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">
                  <v-icon small color="primary">mdi-credit-card</v-icon>
                  Identifiant Carte
                </div>
                <div class="info-value">{{ userInfo.walletCardIdentifiant || 'Non renseigné' }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">
                  <v-icon small color="primary">mdi-cash-check</v-icon>
                  Type de Paiement
                </div>
                <div class="info-value payment-status" :class="{'status-active': userInfo.deferredPayment}">
                  {{ userInfo.deferredPayment ? 'Paiement Différé Activé' : 'Paiement Immédiat' }}
                </div>
              </div>
              <div class="info-item">
                <div class="info-label">
                  <v-icon small color="primary">mdi-lock-outline</v-icon>
                  Statut Verrouillage
                </div>
                <div class="info-value lock-status" :class="{'status-locked': userInfo.locked}">
                  {{ userInfo.locked ? 'Compte Verrouillé' : 'Compte Actif' }}
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </v-card>
  </div>
</template>

<script>
import apiClient from "@/config/api.js";

export default {
  name: 'UserProfile',
  data() {
    return {
      loading: false,
      userInfo: {
        userId: '',
        userIdReal: '',
        accountFirstname: '',
        accountLastname: '',
        accountPhonenumber: '',
        accountEmail: '',
        accountAddress: '',
        realAccountEmail: '',
        userRoles: '',
        brigade: '',
        companyName: '',
        walletId: 0,
        walletCardIdentifiant: '',
        locked: false,
        deferredPayment: false
      },
      error: null,
      alertType: null,
      alertMessage: '',
      showAlertTimer: null,
      elapse: 0
    }
  },
  mounted() {
    this.getData();
  },
  methods: {
    getData() {
      this.loading = true;

      // Check if userId is stored in sessionStorage
      const userId = sessionStorage.getItem("userId");
      if (!userId) {
        this.showAlert('error', 'ID utilisateur non trouvé');
        this.loading = false;
        return;
      }

      // Get baseUrl from store if available
      const baseUrl = this.$store.getters.user.baseUrl

      apiClient
          .get(`${baseUrl}/account/user_by_matricule/${userId}/${userId}`)
          .then(response => {
            this.loading = false;

            if (response.data.status !== 200) {
              this.showAlert('warning', response.data.message || 'Erreur lors du chargement des données');
            } else {
              // Map the data to our user info object
              const userData = response.data.datas;
              if (userData) {
                this.userInfo = {
                  userId: userData.userId || '',
                  userIdReal: userData.userIdReal || '',
                  accountFirstname: this.formatFirstName(userData.accountFirstname) || '',
                  accountLastname: this.formatLastName(userData.accountLastname) || '',
                  accountPhonenumber: userData.accountPhonenumber || '',
                  accountEmail: this.formatEmail(userData.accountEmail) || '',
                  accountAddress: userData.accountAddress || '',
                  realAccountEmail: this.formatEmail(userData.realAccountEmail) || '',
                  userRoles: this.formatRoles(userData.userRoles) || '',
                  brigade: this.formatBrigade(userData.brigade) || '',
                  companyName: userData.companyName || '',
                  walletId: userData.walletId || 0,
                  walletCardIdentifiant: userData.walletCardIdentifiant || '',
                  locked: userData.locked || false,
                  deferredPayment: userData.deferredPayment || false
                };
              } else {
                this.showAlert('error', 'Aucune donnée utilisateur trouvée');
              }
            }
          })
          .catch(error => {
            this.loading = false;
            console.error("Erreur lors du chargement des données:", error);
            this.showAlert('error', 'Échec du chargement des données: ' + (error.message || 'Erreur inconnue'));
          });
    },
    isEmpty(value) {
      return value === null || value === undefined || value === '';
    },
    formatFirstName(firstName) {
      if (this.isEmpty(firstName)) return '';
      return firstName.toLowerCase().split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
    },
    formatLastName(lastName) {
      if (this.isEmpty(lastName)) return '';
      return lastName.toUpperCase();
    },
    formatName(lastName, firstName) {
      const formattedLastName = this.formatLastName(lastName);
      const formattedFirstName = this.formatFirstName(firstName);

      if (formattedLastName && formattedFirstName) {
        return `${formattedLastName} ${formattedFirstName}`;
      } else if (formattedLastName) {
        return formattedLastName;
      } else if (formattedFirstName) {
        return formattedFirstName;
      } else {
        return 'Utilisateur';
      }
    },
    formatEmail(email) {
      if (this.isEmpty(email)) return '';
      return email.toLowerCase();
    },
    formatRoles(roles) {
      if (this.isEmpty(roles)) return '';
      return roles.toUpperCase();
    },
    formatBrigade(brigade) {
      if (this.isEmpty(brigade)) return '';
      return brigade.toUpperCase();
    },
    getInitials(firstName, lastName) {
      let initials = '';
      if (firstName && firstName.length > 0) {
        initials += firstName.charAt(0).toUpperCase();
      }
      if (lastName && lastName.length > 0) {
        initials += lastName.charAt(0).toUpperCase();
      }
      return initials || '?';
    },
    alertColor(type) {
      return type === "info" ? "#4CAF50" : type === "warning" ? "#FF9800" : "#F44336";
    },
    showAlert(type, message) {
      this.alertType = type;
      this.alertMessage = message;

      // Clear any existing timer
      if (this.showAlertTimer) {
        clearTimeout(this.showAlertTimer);
      }

      // Auto-hide after 5 seconds
      this.showAlertTimer = setTimeout(() => {
        this.alertType = null;
      }, 5000);

      this.elapse = 1;
      let t = this.showAlert.t;
      if (t) {
        clearInterval(t);
      }

      this.showAlert.t = setInterval(() => {
        if (this.elapse === 3) {
          this.elapse = 0;
          clearInterval(this.showAlert.t);
        } else {
          this.elapse++;
        }
      }, 5000);
    },
    dismissibleClose() {
      this.elapse = 3;
      clearTimeout(this.showAlertTimer);
      clearInterval(this.showAlert.t);
      this.alertType = null;
    }
  }
}
</script>

<style scoped>
/* Variables CSS pour les couleurs principales */
:root {
  --primary-color: #01579B;
  --secondary-color: #0288D1;
  --text-color: #333;
  --bg-light: rgba(255, 255, 255, 0.95);
}

.user-profile-container {
  width: 100%;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 15px;
}

@media (max-width: 600px) {
  .user-profile-container {
    padding-top: 50px; /* Espace pour la toolbar */
    align-items: flex-start; /* Aligner en haut sur mobile */
  }
}

.profile-card {
  border-radius: 12px;
  overflow: hidden;
  background-color: rgba(255, 255, 255, 0.99) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
  max-width: 700px;
  margin: 0 auto;
  width: 100%;
}

.card-content {
  padding: 15px;
}

.app-title {
  color: #01579B;
  font-size: 24px;
  font-weight: 700;
  letter-spacing: 1px;
  margin-bottom: 0;
  text-transform: uppercase;
}

.app-subtitle {
  color: #546E7A;
  font-weight: 500;
  margin-top: 2px;
  font-size: 14px;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px 0;
}

.profile-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding: 15px;
  background: linear-gradient(135deg, #01579B 0%, #0288D1 100%);
  border-radius: 8px;
  color: white;
}

.profile-header.locked {
  background: linear-gradient(135deg, #F44336 0%, #D32F2F 100%);
}

.avatar-container {
  position: relative;
  margin-right: 20px;
}

.avatar {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background-color: white;
  color: #01579B;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  font-weight: bold;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.lock-badge {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 22px;
  height: 22px;
  background-color: #F44336;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.user-name {
  flex: 1;
}

.user-name h2 {
  margin: 0 0 5px 0;
  font-size: 22px;
  font-weight: 600;
}

.user-role {
  font-size: 14px;
  opacity: 0.9;
  font-weight: 500;
}

.section-card {
  background-color: rgba(1, 87, 155, 0.03);
  border-radius: 8px;
  padding: 10px;
  margin-bottom: 12px;
  border-left: 3px solid #01579B;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 10px;
  color: #01579B;
  font-weight: 600;
  font-size: 13px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 12px;
}

.info-item {
  padding: 10px 12px;
  border-radius: 6px;
  background-color: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
}

.info-item:hover {
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.info-label {
  font-size: 13px;
  color: #546E7A;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.info-value {
  font-size: 15px;
  color: #37474F;
  word-break: break-word;
  font-weight: 500;
}

.payment-status {
  font-weight: 500;
  padding: 3px 8px;
  border-radius: 4px;
  background-color: #ECEFF1;
  display: inline-block;
}

.status-active {
  color: #2E7D32;
  background-color: #E8F5E9;
}

.status-locked {
  color: #D32F2F;
  background-color: #FFEBEE;
}

/* Animation de transition pour les alertes */
.slide-fade-enter-active {
  transition: all 0.3s ease;
}
.slide-fade-leave-active {
  transition: all 0.2s ease;
}
.slide-fade-enter, .slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

/* Styles pour les appareils mobiles */
@media (max-width: 600px) {
  .user-profile-container {
    padding: 10px;
    padding-top: 50px; /* Espace pour la toolbar */
    align-items: flex-start; /* Aligner en haut sur mobile */
  }

  .card-content {
    padding: 15px;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .profile-header {
    flex-direction: column;
    text-align: center;
  }

  .avatar-container {
    margin-right: 0;
    margin-bottom: 15px;
  }
}
</style>